import { create } from "zustand";
import { supabase } from "../supabase/client";
import type { User } from "@/types";
import type { Session } from "@supabase/supabase-js";
import { debugAuthState } from "./debug";
import { authRateLimiter } from "./rate-limiter";
import { authManager } from "./auth-manager";
import { useBookingsStore } from "@/lib/stores/bookings-store";
import { logger } from '@/lib/utils/logger';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: 'google') => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  completeProfile: (updates: Partial<User>) => Promise<User>;
  clearError: () => void;
  ensureSession: () => Promise<Session | null>;
  resetAuth: () => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  error: null,

  setUser: (user) => set({ user, loading: false }),
  setSession: (session) => set({ session }),
  setLoading: (loading) => {
    // Add a failsafe for loading state
    set({ loading });
    
    // If setting to true, add a timeout to prevent getting stuck
    if (loading) {
      setTimeout(() => {
        const currentState = get();
        if (currentState.loading) {
          logger.warn('Auth loading timeout - forcing to false after 3 seconds');
          set({ loading: false });
        }
      }, 3000); // 3 seconds failsafe (reduced from 15 seconds)
    }
  },
  setError: (error) => set({ error, loading: false }),

  signIn: async (email: string, password: string) => {
    // Check rate limit
    if (!authRateLimiter.canMakeRequest('signIn')) {
      set({ loading: false, error: 'Too many sign in attempts. Please wait a moment.' });
      throw new Error('Rate limit exceeded');
    }
    
    // Check if already signing in
    const currentState = get();
    if (currentState.loading) {
      logger.warn('Sign in already in progress, ignoring duplicate attempt');
      return;
    }
    
    // Clear bookings store when signing in (in case it's a different user)
    useBookingsStore.getState().reset();
    
    try {
      set({ loading: true, error: null });
      logger.debug('Attempting sign in:', { email, timestamp: new Date().toISOString() });

      const { data: { user }, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      logger.debug('Sign in response:', { user: user?.id, error: signInError?.message });

      if (signInError) {
        logger.error('Sign in error:', signInError);
        set({ loading: false, error: signInError.message });
        throw signInError;
      }
      if (!user) {
        logger.error('No user returned from sign in');
        set({ loading: false, error: 'No user returned from sign in' });
        throw new Error('No user returned from sign in');
      }

      // Wait for session to be established
      logger.debug('Waiting for session to be established...');
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verify session is properly set
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        logger.error('Session verification failed:', sessionError);
        set({ loading: false, error: 'Session could not be established' });
        throw new Error('Session could not be established');
      }
      logger.info('Session verified successfully for user:', session.user.id);

      // Additional verification - ensure getUser() also works
      const { data: { user: verifyUser }, error: userError } = await supabase.auth.getUser();
      if (userError || !verifyUser) {
        logger.error('User verification failed after session setup:', userError);
        
        // Force session refresh by calling refreshSession
        logger.debug('Attempting to force session refresh...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData.session || !refreshData.user) {
          logger.error('Session refresh failed:', refreshError);
          
          // Last resort: set session manually using the session we verified
          logger.debug('Attempting to set session manually...');
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token
          });
          
          if (setSessionError) {
            logger.error('Manual session set failed:', setSessionError);
            set({ loading: false, error: 'Session could not be established' });
            throw new Error('Session could not be established');
          }
          
          // Verify the manual session worked
          const { data: { user: finalUser }, error: finalError } = await supabase.auth.getUser();
          if (finalError || !finalUser) {
            logger.error('Final session verification failed:', finalError);
            set({ loading: false, error: 'Session verification failed' });
            throw new Error('Session verification failed');
          }
          logger.info('Manual session set successful for user:', finalUser.id);
        } else {
          logger.info('Session refreshed successfully for user:', refreshData.user.id);
        }
      } else {
        logger.debug('User verification successful:', verifyUser.id);
      }

      // Fetch complete user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select(`
          *,
          creator_profile:creator_profiles(*)
        `)
        .eq('id', user.id)
        .single();

      if (profileError) {
        logger.error('Profile fetch error:', profileError);
        set({ loading: false, error: profileError.message });
        throw profileError;
      }
      if (!profile) {
        logger.error('Failed to fetch user profile');
        set({ loading: false, error: 'Failed to fetch user profile' });
        throw new Error('Failed to fetch user profile');
      }

      const mappedUser = mapSupabaseUserToAppUser(profile);
      set({ user: mappedUser, session, loading: false, error: null });
      logger.info('Sign in successful, user and session set:', { userId: mappedUser.id, sessionUserId: session?.user?.id });

      // Auto-claim tickets functionality has been permanently moved to manual claim flow
      // Users must explicitly claim tickets through the ClaimTicket page for security reasons
    } catch (error) {
      logger.error('Sign in error (outer catch):', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign in'
      });
      throw error;
    }
  },

  signInWithProvider: async (provider: 'google') => {
    try {
      set({ loading: true, error: null });

      const { data, error: signInError } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signInError) {
        logger.error('OAuth sign in error:', signInError);
        set({ loading: false, error: signInError.message });
        throw signInError;
      }

      // OAuth will redirect, so we don't need to handle the success case here
      set({ loading: false });
    } catch (error) {
      logger.error('OAuth sign in error (outer catch):', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign in with OAuth'
      });
      throw error;
    }
  },

  signUp: async (email: string, password: string, fullName: string) => {
    try {
      set({ loading: true, error: null });

      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!user) throw new Error('No user returned from sign up');

      // Create user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .insert({
          id: user.id,
          email: user.email,
          full_name: fullName,
          role: 'user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('Failed to create user profile');

      set({ user: mapSupabaseUserToAppUser(profile), loading: false, error: null });
    } catch (error) {
      logger.error('Sign up error:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign up'
      });
      throw error;
    }
  },

  signOut: async () => {
    try {
      set({ loading: true, error: null });

      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;

      // Clear all stores to prevent showing data from previous user
      useBookingsStore.getState().reset();
      
      set({ user: null, session: null, loading: false, error: null });
    } catch (error) {
      logger.error('Sign out error:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign out'
      });
      throw error;
    }
  },

  completeProfile: async (updates: Partial<User>) => {
    try {
      const user = get().user;
      if (!user) throw new Error("Not authenticated");

      set({ loading: true, error: null });

      // Update user profile
      const { data: updatedUser, error: updateError } = await supabase
        .from("users")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select(
          `
          *,
          creator_profile:creator_profiles(*)
        `,
        )
        .single();

      if (updateError) throw updateError;
      if (!updatedUser) throw new Error("Failed to update profile");

      set({ user: mapSupabaseUserToAppUser(updatedUser), loading: false, error: null });
      return updatedUser;
    } catch (error) {
      logger.error("Profile update error:", error);
      set({
        loading: false,
        error:
          error instanceof Error ? error.message : "Failed to update profile",
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  ensureSession: async () => {
    // Check rate limit for session checks
    if (!authRateLimiter.canMakeRequest('ensureSession')) {
      logger.warn('Too many session verification attempts');
      const currentState = get();
      return currentState.session; // Return existing session if available
    }
    
    try {
      const currentState = get();
      if (currentState.session) return currentState.session;

      set({ loading: true, error: null });

      const { data: { session: supabaseSession }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !supabaseSession) {
        logger.error('Session verification failed:', sessionError);
        set({ loading: false, error: 'Session could not be established' });
        throw new Error('Session could not be established');
      }
      logger.info('Session verified successfully for user:', supabaseSession.user.id);

      set({ session: supabaseSession, loading: false, error: null });
      return supabaseSession;
    } catch (error) {
      logger.error('Session verification error:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to verify session'
      });
      throw error;
    }
  },

  resetAuth: () => {
    logger.debug('Resetting auth state...');
    set({ 
      user: null, 
      session: null, 
      loading: false, 
      error: null 
    });
  }
}));

function mapSupabaseUserToAppUser(supabaseUser: any): User {
  return {
    ...supabaseUser,
    status: supabaseUser.status || 'active', // default to 'active' if missing
  };
}

// Initialize auth connection after all modules are loaded
// This avoids circular dependency issues
export function initializeAuthConnection() {
  // Connect auth manager to store
  // This ensures the store is updated when auth state changes
  authManager.subscribe(async (session) => {
    const store = useAuth.getState();
    
    if (session?.user) {
      // Session exists, update store
      store.setSession(session);
      
      // Only fetch profile if we don't have it
      if (!store.user || store.user.id !== session.user.id) {
        try {
          const { data: profile, error } = await supabase
            .from('users')
            .select(`
              *,
              creator_profile:creator_profiles(*)
            `)
            .eq('id', session.user.id)
            .single();
            
          if (profile && !error) {
            store.setUser(mapSupabaseUserToAppUser(profile));
            store.setLoading(false);
          } else {
            store.setLoading(false);
          }
        } catch (error) {
          logger.error('Failed to fetch profile in auth sync:', error);
          store.setLoading(false);
        }
      } else {
        store.setLoading(false);
      }
    } else {
      // No session, clear store
      store.setUser(null);
      store.setSession(null);
      store.setLoading(false);
    }
  });

  // Initialize loading state based on auth manager
  if (!authManager.isInitialized()) {
    // Auth manager is still initializing
    useAuth.getState().setLoading(true);
  } else {
    // Auth manager is ready, sync initial state
    const session = authManager.getSession();
    if (session) {
      authManager.subscribe(() => {}); // Trigger the subscriber above
    } else {
      useAuth.getState().setLoading(false);
    }
  }
}

// Call this function once the app initializes
if (typeof window !== 'undefined') {
  // Use setTimeout to ensure all modules are loaded
  setTimeout(() => {
    initializeAuthConnection();
  }, 0);
}